import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { utils, writeFile } from 'xlsx';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import OutlineButton from 'components/OutlineButton';
import SolidButton from 'components/SolidButton';
import Table from 'components/Table';

import { CUSTOMER_LEVELS } from 'utils/constants';
import { ICustomer } from 'utils/interfaces';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function CustomersPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  const [loadingId, setLoadingId] = useState('');
  const [customers, setCustomers] = useState<Array<ICustomer>>([]);

  const toggleCustomer = async (userId: string, isActive: boolean) => {
    setLoadingId(userId);
    let result: any = await generalApi.post('/toggle_user', {
      userId,
    });
    setLoadingId('');
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(isActive ? 'Cliente deshabilitado' : 'Cliente habilitado');
    setRefresh(true);
  };

  const activateCustomer = async (userId: string) => {
    setLoadingId(userId);
    let result: any = await generalApi.get(`/confirm_access/${userId}`);
    setLoadingId('');
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('Cliente activado');
    setRefresh(true);
  };

  const exportData = async () => {
    const result = await generalApi.get('/users?page=1&limit=10000000');
    const data = result.data.docs;
    if (data.length === 0) return showErrorToast('No hay clientes para exportar');
    var createXLSLFormatObj = [];
    var xlsHeader = [
      'N° Cliente',
      'Tipo',
      'RUC',
      'Empresa',
      'Número de Documento',
      'Nombres',
      'Apellidos',
      'Celular',
      'Correo',
      'Dirección',
      'PEP Institución',
      'PEP Fecha de Inicio',
    ];
    createXLSLFormatObj.push(xlsHeader);
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const innerRowData = [];
      innerRowData.push(i + 1 + '');
      innerRowData.push(CUSTOMER_LEVELS[item.level as keyof typeof CUSTOMER_LEVELS]);
      innerRowData.push(item.level === '31' ? item.username.split('|')[1] : '-');
      innerRowData.push(item.company_name);
      innerRowData.push(item.username.split('|')[0] || '-');
      innerRowData.push(item.name);
      innerRowData.push(item.lname_p);
      innerRowData.push(item.cellphone);
      innerRowData.push(item.email);
      innerRowData.push(item.address);
      if (item.pep != null) {
        innerRowData.push(item.pep.institute);
        innerRowData.push(item.pep.since);
      }
      createXLSLFormatObj.push(innerRowData);
    }
    const filename = 'Reporte.xlsx';
    const ws_name = 'Clientes';
    const wb = utils.book_new(),
      ws = utils.aoa_to_sheet(createXLSLFormatObj);
    utils.book_append_sheet(wb, ws, ws_name);
    writeFile(wb, filename);
  };

  useEffect(() => {
    const getCustomers = async () => {
      setLoading(true);
      const result = await generalApi.get(`/users?page=${currentPage}&limit=10`);
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setCustomers(result.data.docs);
      setTotalDocs(result.data.totalDocs);
      setLoading(false);
    };
    if (refresh) {
      getCustomers();
      setRefresh(false);
    }
  }, [generalApi, refresh, currentPage]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Lista de clientes</p>
            <div className="space-y-2 sm:ml-20 sm:flex sm:space-y-0 sm:space-x-4">
              <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => exportData()}>
                Exportar
              </OutlineButton>
            </div>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando clientes"
            initialSort={{
              key: 'created_at',
              order: 'desc',
            }}
            currentPage={currentPage}
            totalDocs={totalDocs}
            onPageChange={(page) => {
              setCurrentPage(page);
              setRefresh(true);
            }}
            columns={[
              {
                title: 'TIPO',
                dataKey: 'level',
                sortValue: (item: ICustomer) =>
                  CUSTOMER_LEVELS[item.level as keyof typeof CUSTOMER_LEVELS],
                render: (item: ICustomer) =>
                  CUSTOMER_LEVELS[item.level as keyof typeof CUSTOMER_LEVELS],
              },
              {
                title: 'RUC',
                key: 'ruc',
                dataKey: 'username',
                sortValue: (item: ICustomer) =>
                  item.level !== '30' ? item.username.split('|')[1] : '',
                render: (item: ICustomer) =>
                  item.level !== '30' ? item.username.split('|')[1] : '-',
              },
              {
                title: 'EMPRESA',
                dataKey: 'company_name',
              },
              {
                title: 'N° DOCUMENTO',
                key: 'doc_number',
                dataKey: 'username',
                render: (item: ICustomer) => item.username.split('|')[0] || '-',
              },
              {
                title: 'NOMBRES',
                dataKey: 'name',
              },
              {
                title: 'APELLIDOS',
                dataKey: 'lname_p',
                render: (item: ICustomer) => `${item.lname_p} ${item.lname_m}`,
              },
              {
                title: 'Celular',
                dataKey: 'cellphone',
              },
              {
                title: 'Correo',
                dataKey: 'email',
              },
              {
                title: 'Dirección',
                dataKey: 'address',
              },
              {
                title: 'OPCIONES',
                dataKey: 'options',
                disableSort: true,
                render: (item: ICustomer) => (
                  <div className="flex space-x-2 pr-2">
                    <SolidButton
                      isLoading={loadingId === item._id}
                      color={item.is_active ? BUTTON_COLORS.RED : BUTTON_COLORS.BLUE}
                      onClick={() => toggleCustomer(item._id, item.is_active)}
                    >
                      {item.is_active ? 'Deshabilitar' : 'Habilitar'}
                    </SolidButton>
                    {!item.confirmed_access && (
                      <SolidButton
                        color={BUTTON_COLORS.BLUE}
                        onClick={() => activateCustomer(item._id)}
                      >
                        Activar
                      </SolidButton>
                    )}
                  </div>
                ),
              },
            ]}
            data={customers}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
